import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import api from '../../utils/api'

const FinanceStoreContext = createContext(null)

const safe = async (fn, fallback) => {
  try { return await fn() } catch { return fallback }
}

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

export function FinanceStoreProvider({ children }) {
  const [invoices, setInvoices] = useState([])
  const [adminFees, setAdminFees] = useState([])
  const [payouts, setPayouts] = useState([])
  const [loading, setLoading] = useState(false)
  const [summaryLoaded, setSummaryLoaded] = useState(false)
  const [adminFeesLoaded, setAdminFeesLoaded] = useState(false)
  const summaryInflightRef = useRef(null)
  const adminFeesInflightRef = useRef(null)

  const ensureSummaryLoadedRef = useRef(null)
  ensureSummaryLoadedRef.current = async ({ force = false } = {}) => {
    if (summaryLoaded && !force) return
    if (summaryInflightRef.current && !force) return summaryInflightRef.current

    const run = (async () => {
      setLoading(true)
      const nextInvoices = asArray(await safe(() => api.invoices.list(), []))
      const nextPayouts = asArray(await safe(() => api.payouts.list(), []))
      setInvoices(nextInvoices)
      setPayouts(nextPayouts)
      setSummaryLoaded(true)
      setLoading(false)
    })()

    summaryInflightRef.current = run
    await run
    summaryInflightRef.current = null
  }

  const ensureSummaryLoaded = useCallback((o) => ensureSummaryLoadedRef.current(o), [])

  const ensureAdminFeesLoadedRef = useRef(null)
  ensureAdminFeesLoadedRef.current = async ({ force = false, employerId } = {}) => {
    if (adminFeesLoaded && !force) return
    if (adminFeesInflightRef.current && !force) return adminFeesInflightRef.current

    const run = (async () => {
      setLoading(true)
      const query = employerId ? { employer_id: employerId } : undefined
      const next = asArray(await safe(() => api.adminFees.list(query), []))
      setAdminFees(next)
      setAdminFeesLoaded(true)
      setLoading(false)
    })()

    adminFeesInflightRef.current = run
    await run
    adminFeesInflightRef.current = null
  }

  const ensureAdminFeesLoaded = useCallback((o) => ensureAdminFeesLoadedRef.current(o), [])

  const ensureLoadedRef = useRef(null)
  ensureLoadedRef.current = async ({ force = false, employerId } = {}) => {
    await ensureSummaryLoaded({ force })
    await ensureAdminFeesLoaded({ force, employerId })
  }

  const ensureLoaded = useCallback((o) => ensureLoadedRef.current(o), [])

  const addAdminFee = async (payload) => {
    const created = await api.adminFees.create(payload)
    await ensureAdminFeesLoaded({ force: true })
    return created
  }

  const generateAdminFeeInvoice = async (id) => {
    const result = await api.adminFees.generateInvoice(id)
    await ensureSummaryLoaded({ force: true })
    return result
  }

  const getInvoice = (id) => api.invoices.get(id)

  const runInvoicing = async ({ forDate } = {}) => {
    const body = forDate ? { for_date: forDate } : {}
    const report = await api.invoices.run(body)
    await ensureSummaryLoaded({ force: true })
    return report
  }

  const sendInvoice = async (id) => {
    const updated = await api.invoices.send(id)
    await ensureSummaryLoaded({ force: true })
    return updated
  }

  const markInvoicePaid = async (id) => {
    const updated = await api.invoices.markPaid(id)
    await ensureSummaryLoaded({ force: true })
    return updated
  }

  const updateAdminFeeStatus = async (id, status) => {
    const updated = await api.adminFees.updateStatus(id, { status })
    await ensureSummaryLoaded({ force: true })
    await ensureAdminFeesLoaded({ force: true })
    return updated
  }

  const markAdminFeePaid = async (id) => {
    const updated = await api.adminFees.markPaid(id)
    await ensureSummaryLoaded({ force: true })
    await ensureAdminFeesLoaded({ force: true })
    return updated
  }

  const updatePayoutStatus = async ({ id, status }) => {
    if (status === 'processing') await api.payouts.initiate(id)
    else if (status === 'paid') await api.payouts.markPaid(id)
    await ensureLoaded({ force: true })
  }

  const value = useMemo(() => ({
    invoices,
    adminFees,
    payouts,
    loading,
    summaryLoaded,
    adminFeesLoaded,
    ensureLoaded,
    ensureSummaryLoaded,
    ensureAdminFeesLoaded,
    addAdminFee,
    generateAdminFeeInvoice,
    getInvoice,
    runInvoicing,
    sendInvoice,
    markInvoicePaid,
    updateAdminFeeStatus,
    markAdminFeePaid,
    updatePayoutStatus,
  }), [invoices, adminFees, payouts, loading, summaryLoaded, adminFeesLoaded])

  return (
    <FinanceStoreContext.Provider value={value}>
      {children}
    </FinanceStoreContext.Provider>
  )
}

export function useFinanceStore() {
  const ctx = useContext(FinanceStoreContext)
  if (!ctx) throw new Error('useFinanceStore must be used within FinanceStoreProvider')
  return ctx
}
